import React, { useState, useCallback } from 'react';
import {
  PaymentElement,
  useStripe,
  useElements,
  AddressElement
} from '@stripe/react-stripe-js';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import { ArrowLeft, AlertCircle, Info, CreditCard, Shield, Lock } from 'lucide-react';

const TransferPaymentForm = ({ 
  reviewData, 
  onBack, 
  onPaymentSuccess, 
  setLoading, 
  isLoading, 
  hideInfoBox = false 
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { user } = useAuth0();

  const [isProcessing, setIsProcessing] = useState(false);
  const [isElementsLoading, setIsElementsLoading] = useState(true);
  const [paymentError, setPaymentError] = useState(null);
  const [shippingAddress, setShippingAddress] = useState(null);

  // Get the correct eSIM status - check both legacy and new field names
  const isEsim = reviewData?.userDetails?.hasEsim || reviewData?.userDetails?.esim || false;

  // Check if ALL lines are eSIM
  const isAllLinesEsim = reviewData?.userDetails?.lineDetails && 
    reviewData.userDetails.lineDetails.length > 0 && 
    reviewData.userDetails.lineDetails.every(line => line.esim);

  // Determine if shipping address is needed - don't need shipping if all lines are eSIM
  const needsShippingAddress = !isAllLinesEsim && !isEsim;

  const validatePaymentDetails = useCallback(() => {
    if (!stripe || !elements) {
      setPaymentError('Payment system is not fully loaded. Please wait.');
      return false;
    }
  
    if (!reviewData?.planDetails?.totalActivationFee) {
      setPaymentError('Invalid payment amount.');
      return false;
    }
  
    if (!reviewData?.subscriptionDetails?.type) {
      setPaymentError('Subscription type is required.');
      return false;
    }
  
    // Add circle validation
    if (!reviewData?.userDetails?.circle_id || !reviewData?.userDetails?.circle_name) {
      setPaymentError('Circle information is missing. Please try again.');
      console.error('Missing circle information:', {
        circle_id: reviewData?.userDetails?.circle_id,
        circle_name: reviewData?.userDetails?.circle_name
      });
      return false;
    }
  
    // Only validate shipping address if physical SIM is needed
    if (needsShippingAddress && !shippingAddress) {
      setPaymentError('Shipping address is required for physical SIM cards.');
      return false;
    }
  
    return true;
  }, [stripe, elements, reviewData, needsShippingAddress, shippingAddress]);

  const handlePayment = async (e) => {
    e.preventDefault();
  
    if (!validatePaymentDetails()) {
      return;
    }
  
    try {
      setIsProcessing(true);
      setPaymentError(null);
      
      // If parent provided a loading setter, use it
      if (setLoading) {
        setLoading(true);
      }
  
      const { error: submitError } = await elements.submit();
      if (submitError) {
        throw new Error(submitError.message);
      }
      
      // This will call the parent's onPaymentSuccess handler which uses the payment service
      if (onPaymentSuccess) {
        // Get payment method ID from Stripe Elements
        const { error, paymentMethod } = await stripe.createPaymentMethod({
          elements,
          params: {
            billing_details: {
              name: user?.name || user?.email,
              email: user?.email,
              // Include address if available
              ...(shippingAddress?.address && {
                address: shippingAddress.address,
                phone: shippingAddress.phone
              })
            }
          }
        });
        
        if (error) {
          throw new Error(error.message);
        }
        
        // Pass payment method ID to parent component
        await onPaymentSuccess(paymentMethod.id);
      }
    } catch (err) {
      console.error('Payment processing error:', err);
      setPaymentError(err.message || 'An error occurred while processing your payment.');
    } finally {
      setIsProcessing(false);
      if (setLoading) {
        setLoading(false);
      }
    }
  };

  if (!reviewData) {
    return (
      <Alert
        severity="error"
        icon={<AlertCircle />}
      >
        Payment information is missing. Please try again.
      </Alert>
    );
  }

  // Calculate the total amount from review data
  const totalAmount = reviewData.planDetails.totalActivationFee + 
    (isEsim ? 0 : (reviewData.subscriptionDetails.shipping === 'expedited' ? 15 : 8));

  return (
    <Box className="payment-form">
      {/* Transfer-specific info box - only show if not hidden by parent */}
      {!hideInfoBox && (
        <Box sx={{ 
          p: 2, 
          mb: 3, 
          bgcolor: '#fef2f2', 
          borderRadius: 2,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2
        }}>
          <Info size={20} color="#ff385c" />
          <Box>
            <Typography variant="subtitle1" fontWeight={600} color="#e11d48">
              Transfer Subscription
            </Typography>
            <Typography variant="body2">
              You're only paying for the carrier activation fee. Your CircledIn subscription will remain active.
            </Typography>
          </Box>
        </Box>
      )}

      {paymentError && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          icon={<AlertCircle />}
        >
          {paymentError}
        </Alert>
      )}

      {needsShippingAddress && (
        <>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom fontWeight={600}>
              Shipping Address
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Please enter your shipping address for the physical SIM card.
            </Typography>
            <AddressElement
              options={{
                mode: 'shipping',
                allowedCountries: ['US'],
                fields: { phone: 'always' },
                validation: {
                  phone: {
                    required: 'always',
                  },
                },
                defaultValues: {
                  name: user?.name || user?.email,
                  address: {
                    country: 'US',
                  },
                },
              }}
              onChange={(event) => {
                if (event.complete) {
                  setShippingAddress(event.value);
                }
              }}
            />
          </Box>
          <Divider sx={{ my: 4 }} />
        </>
      )}

      <Box sx={{ mb: 4 }}>
        <Box sx={{ position: 'relative', mt: 2 }}>
          {isElementsLoading && (
            <Box sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1
            }}>
              <CircularProgress size={24} />
            </Box>
          )}
          <PaymentElement
            options={{
              layout: 'tabs',
              paymentMethodOrder: ['card'],
              fields: {
                billingDetails: 'never'
              },
              defaultValues: {
                billingDetails: {
                  address: {
                    country: 'US',
                  }
                }
              }
            }}
            onReady={() => setIsElementsLoading(false)}
            onChange={(event) => {
              if (event.error) {
                setPaymentError(event.error.message);
              } else {
                setPaymentError(null);
              }
            }}
          />
        </Box>
      </Box>

      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 2,
        mt: 4
      }}>
        <Button
          onClick={onBack}
          startIcon={<ArrowLeft size={18} />}
          variant="outlined"
          disabled={isProcessing || isLoading}
          sx={{
            color: 'text.secondary',
            borderColor: 'divider',
            '&:hover': {
              borderColor: 'text.secondary'
            },
            padding: '8px 16px',
          }}
        >
          Back
        </Button>

        <Button
          onClick={handlePayment}
          variant="contained"
          disabled={isProcessing || isLoading || (needsShippingAddress && !shippingAddress)}
          sx={{
            bgcolor: '#FF4775',
            color: 'white',
            '&:hover': {
              bgcolor: '#FF385C'
            },
            '&:disabled': {
              bgcolor: '#FCA5A5'
            },
            padding: '8px 20px',
          }}
        >
          {isProcessing || isLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} sx={{ color: 'white' }} />
              Processing...
            </Box>
          ) : (
            `Pay $${totalAmount.toFixed(2)}`
          )}
        </Button>
      </Box>
      
      {/* Security information section */}
      <Box sx={{ mt: 6, mb: 2 }}>
        <Typography 
          variant="subtitle2" 
          color="text.secondary" 
          sx={{ mb: 2 }}
        >
          Why is this secure?
        </Typography>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ color: '#ef4444' }}>
              <AlertCircle size={18} />
            </Box>
            <Typography variant="body2" color="text.secondary">
              All payments are encrypted and processed securely
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ color: '#ef4444' }}>
              <CreditCard size={18} />
            </Box>
            <Typography variant="body2" color="text.secondary">
              Your payment details are never stored on our servers
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ color: '#ef4444' }}>
              <Shield size={18} />
            </Box>
            <Typography variant="body2" color="text.secondary">
              We use industry-standard security protocols
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default TransferPaymentForm;